import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Form, Input, Progress, Select, Space, Tag, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { ComponentProps, ComponentType } from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import {
    Button as ZhButton,
    Dialog as ZhDialog,
    InputNumber as ZhInputNumber,
    Table as ZhTable,
    type TableColumnProps
} from "zhihao-ui-react";

import request from "@/api";
import { enterpriseManageService } from "@/api/services/enterpriseManageService";
import { prodTypeService } from "@/api/services/prodTypeService";
import { clearDaiBanState, dateTimeFormat, getDaiBanState } from "@/utils";
import { getEnterpriseName } from "@/utils/customer";
import { useUserStore } from "@/views/stores";
import { TableImageCell } from "@/views/components/TableImageCell";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { formatCurrency } from "@zhihaoscm/utils";
import AssignDialog, { type assignSubmitData, type Officer } from "@/views/components/AssignDialog/AssignDialog";
import type { ConsignmentQuery, GroupPurchasePageResult, GroupPurchaseRow } from "./types";

type GroupPurchaseQuery = Omit<ConsignmentQuery, "publishState"> & {
    groupPurchaseState?: number;
    publishState?: number[];
};

const defaultQuery: GroupPurchaseQuery = { page: 1, size: 10 };

// beta.13 的运行时支持 controls 配置，类型声明暂未暴露该兼容字段。
type ProductWeightInputProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const ProductWeightInput = ZhInputNumber as ComponentType<ProductWeightInputProps>;
const groupStateLabels: Record<number, string> = { 1: "拼团中", 2: "拼团成功", 3: "拼团失败" };
const productStateLabels: Record<number, string> = { 1: "上架", 2: "下架" };
const publishStateLabels: Record<number, string> = {
    1: "待审核",
    2: "未通过",
    3: "已发布",
    4: "已下架",
    5: "已关闭",
    6: "待提交"
};

type GroupPurchaseFilterValues = {
    keyword?: string;
    productTypeId?: string;
    supplierId?: number;
    groupPurchaseState?: number;
    state?: number;
    publishState?: number[];
    handlerName?: string;
};

type GroupPurchaseFilterOption = { label: string; value: number | string };

type GroupPurchaseSpecialist = {
    user: { personId?: number; name?: string; employeeId?: string };
    productCount?: number;
};

/** Vue 下架确认弹窗展示的团购订单统计契约。 */
export interface GroupPurchaseDownShelfInfo {
    totalOrderNum?: number;
    obligationOrderNum?: number;
    toBeConfirmedOrderNum?: number;
    groupingOrderNum?: number;
}

export interface GroupPurchasePageProps {
    assignGroupPurchase?: (id: number | string, handlerId: number) => Promise<void>;
    closeGroupPurchase?: (id: number | string, unActiveFileIds?: number[]) => Promise<void>;
    downShelfGroupPurchase?: (id: number | string) => Promise<void>;
    findServiceSpecials?: (keyword: string) => Promise<GroupPurchaseSpecialist[]>;
    loadDownShelfInfo?: (id: number | string) => Promise<GroupPurchaseDownShelfInfo>;
    loadGroupPurchases?: (query: GroupPurchaseQuery) => Promise<GroupPurchasePageResult>;
    loadProductTypeOptions?: (keyword: string) => Promise<GroupPurchaseFilterOption[]>;
    loadSupplierOptions?: (keyword: string) => Promise<GroupPurchaseFilterOption[]>;
    updateWeight?: (params: { id: number | string; weight: number }) => Promise<void>;
    permissions?: string[];
}

/** 复用 Vue 团购商品分页接口。 */
export async function loadGroupPurchases(query: GroupPurchaseQuery): Promise<GroupPurchasePageResult> {
    const response = await request.service.get<GroupPurchasePageResult>("/admin/product-group-purchase/paging", {
        params: query
    });
    return response.data;
}

/** 复用 Vue 团购列表的品类远程选择接口，商品类型固定为团购。 */
export async function loadGroupPurchaseProductTypeOptions(keyword: string): Promise<GroupPurchaseFilterOption[]> {
    const rows = await prodTypeService.selectorInProduct({ keyword: keyword || undefined, productType: 2 });
    return rows.map(row => ({
        label: row.areaAbbreviation ? `${row.name} | ${row.areaAbbreviation}` : row.name,
        value: String(row.id)
    }));
}

/** 复用 Vue 团购列表的供应商远程选择接口，商品类型固定为团购。 */
export async function loadGroupPurchaseSupplierOptions(keyword: string): Promise<GroupPurchaseFilterOption[]> {
    const rows = await enterpriseManageService.selectorByProduct({ name: keyword || undefined, productType: 2 });
    return rows.map(row => ({ label: row.supplierName || "--", value: Number(row.id) }));
}

/** 读取一次待办带入的发布状态，保持 Vue init-param 的首屏筛选。 */
function getInitialGroupPurchaseQuery(): GroupPurchaseQuery {
    const publishState = getDaiBanState("groupPublishState").map(Number).filter(Number.isFinite);
    return publishState.length ? { ...defaultQuery, publishState } : defaultQuery;
}

/** 复用 Vue 下架确认弹窗查询参团订单统计的详情接口。 */
export async function loadGroupPurchaseDownShelfInfo(id: number | string): Promise<GroupPurchaseDownShelfInfo> {
    const response = await request.service.get<GroupPurchaseDownShelfInfo>(`/admin/product-group-purchase/vo/${id}`);
    return response.data;
}

/** 复用 Vue 团购商品权重更新接口。 */
export async function updateGroupPurchaseWeight(params: { id: number | string; weight: number }): Promise<void> {
    await request.service.put(`/admin/product-group-purchase/weight/${params.id}`, params);
}

/** 复用 Vue 团购商品下架接口。 */
export async function downShelfGroupPurchase(id: number | string): Promise<void> {
    await request.service.put(`/admin/product-group-purchase/down-shelf/${id}`);
}

/** 复用 Vue 团购商品关闭接口，并提交需要解除关联的文件 ID。 */
export async function closeGroupPurchase(id: number | string, unActiveFileIds: number[] = []): Promise<void> {
    await request.service.put(`/admin/product-group-purchase/close/${id}`, { unActiveFileIds });
}

/** 复用 Vue 团购专员候选查询接口。 */
export async function findServiceSpecials(keyword: string): Promise<GroupPurchaseSpecialist[]> {
    const response = await request.service.get<GroupPurchaseSpecialist[]>(
        `/admin/product-group-purchase/find/specials?name=${encodeURIComponent(keyword)}`
    );
    return response.data;
}

/** 复用 Vue 团购商品指派接口。 */
export async function assignGroupPurchase(id: number | string, handlerId: number): Promise<void> {
    await request.service.get(`/admin/product-group-purchase/assign/${id}/${handlerId}`);
}

/** 将 Vue 团购商品列表返回的团购、商品和发布状态枚举统一为数字。 */
function normalizeGroupPurchaseRows(rows?: GroupPurchaseRow[]): GroupPurchaseRow[] {
    return (rows || []).map(row => ({
        ...row,
        productGroupPurchase: {
            ...row.productGroupPurchase,
            groupPurchaseState:
                row.productGroupPurchase.groupPurchaseState == null
                    ? undefined
                    : Number(row.productGroupPurchase.groupPurchaseState),
            state: row.productGroupPurchase.state == null ? undefined : Number(row.productGroupPurchase.state),
            publishState:
                row.productGroupPurchase.publishState == null
                    ? undefined
                    : Number(row.productGroupPurchase.publishState)
        }
    }));
}

/** 团购商品列表，完整复用旧 Vue 的筛选、表格列、权限与复制入口。 */
export function GroupPurchasePage({
    assignGroupPurchase: assign = assignGroupPurchase,
    closeGroupPurchase: close = closeGroupPurchase,
    downShelfGroupPurchase: removeFromShelf = downShelfGroupPurchase,
    findServiceSpecials: findSpecials = findServiceSpecials,
    loadDownShelfInfo: loadInfo = loadGroupPurchaseDownShelfInfo,
    loadGroupPurchases: load = loadGroupPurchases,
    loadProductTypeOptions: loadProductTypes = loadGroupPurchaseProductTypeOptions,
    loadSupplierOptions: loadSuppliers = loadGroupPurchaseSupplierOptions,
    updateWeight: saveWeight = updateGroupPurchaseWeight,
    permissions
}: GroupPurchasePageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PRODUCT_DEAL");
    const canManage = currentPermissions.includes("ROLE_PRODUCT_MANAGE");
    const canClosePermission = currentPermissions.includes("ROLE_PRODUCT_CLOSE");
    const [items, setItems] = useState<GroupPurchaseRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [assignTarget, setAssignTarget] = useState<GroupPurchaseRow | null>(null);
    const [specialists, setSpecialists] = useState<GroupPurchaseSpecialist[]>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState<number | undefined>();
    const [assignError, setAssignError] = useState<string | null>(null);
    const [assignLoading, setAssignLoading] = useState(false);
    const [downShelfTargetId, setDownShelfTargetId] = useState<number | string | null>(null);
    const [downShelfInfo, setDownShelfInfo] = useState<GroupPurchaseDownShelfInfo>();
    const [downShelfLoading, setDownShelfLoading] = useState(false);
    const [downShelfError, setDownShelfError] = useState<string | null>(null);
    const [query, setQuery] = useState<GroupPurchaseQuery>(getInitialGroupPurchaseQuery);
    const [editingWeightId, setEditingWeightId] = useState<number | string | null>(null);
    const [editingWeightValue, setEditingWeightValue] = useState<number | string>("");
    const [filterForm] = Form.useForm<GroupPurchaseFilterValues>();
    const [productTypeOptions, setProductTypeOptions] = useState<GroupPurchaseFilterOption[]>([]);
    const [productTypeLoading, setProductTypeLoading] = useState(false);
    const [supplierOptions, setSupplierOptions] = useState<GroupPurchaseFilterOption[]>([]);
    const [supplierLoading, setSupplierLoading] = useState(false);

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setItems(normalizeGroupPurchaseRows(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("团购商品加载失败");
                    setItems([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    // 将旧 Vue 七项筛选完整转换为团购分页接口参数，空值不提交。
    const submitFilters = (values: GroupPurchaseFilterValues) => {
        const publishState = values.publishState?.map(Number).filter(Number.isFinite);
        clearDaiBanState(publishState || [], "groupPublishState");
        setErrorMessage(null);
        setQuery({
            ...defaultQuery,
            ...(values.keyword?.trim() ? { keyword: values.keyword.trim() } : {}),
            ...(values.productTypeId ? { productTypeId: values.productTypeId } : {}),
            ...(values.supplierId ? { supplierId: Number(values.supplierId) } : {}),
            ...(values.groupPurchaseState ? { groupPurchaseState: Number(values.groupPurchaseState) } : {}),
            ...(values.state ? { state: Number(values.state) } : {}),
            ...(publishState?.length ? { publishState } : {}),
            ...(values.handlerName?.trim() ? { handlerName: values.handlerName.trim() } : {})
        });
    };

    // 清空全部筛选与待办缓存，并恢复旧 Vue 首屏分页参数。
    const resetFilters = () => {
        clearDaiBanState([], "groupPublishState");
        filterForm.resetFields();
        filterForm.setFieldValue("publishState", []);
        setProductTypeOptions([]);
        setSupplierOptions([]);
        setQuery(defaultQuery);
    };

    /** 按 Vue SelectRemote 契约查询团购品类候选。 */
    const searchProductTypes = async (keyword: string) => {
        setProductTypeLoading(true);
        try {
            setProductTypeOptions(await loadProductTypes(keyword.trim()));
        } catch {
            setProductTypeOptions([]);
        } finally {
            setProductTypeLoading(false);
        }
    };

    /** 按 Vue SelectRemote 契约查询团购供应商候选。 */
    const searchSuppliers = async (keyword: string) => {
        setSupplierLoading(true);
        try {
            setSupplierOptions(await loadSuppliers(keyword.trim()));
        } catch {
            setSupplierOptions([]);
        } finally {
            setSupplierLoading(false);
        }
    };

    /** 将 Vue 三个自定义排序列映射为团购分页接口字段。 */
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            const sortKeyMap: Record<string, string> = {
                "productGroupPurchase.weight": "weight",
                groupEndTime: "group_end_time",
                updatedTime: "updated_time"
            };
            if (!prop || !sortKeyMap[prop]) return;
            setQuery(currentQuery => {
                const nextQuery = { ...currentQuery, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = sortKeyMap[prop];
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 列契约承接 Vue 团购商品的嵌套字段、状态枚举和权限动作。
    const columns: TableColumnProps<GroupPurchaseRow>[] = [
        {
            key: "id",
            label: "商品编号",
            width: 176,
            prop: "productGroupPurchase.id",
            render: ({ row }) => (
                <Button
                    aria-label="查看团购商品详情"
                    type="link"
                    onClick={() =>
                        void navigate(
                            `/productManagement/groupPurchase/detail?id=${row.productGroupPurchase.id || ""}&time=${Date.now()}`
                        )
                    }
                >
                    {row.productGroupPurchase.id || "--"}
                </Button>
            )
        },
        {
            key: "weight",
            label: "权重值",
            width: 176,
            sortable: true,
            prop: "productGroupPurchase.weight",
            render: ({ row }) => {
                const id = row.productGroupPurchase.id;
                if (id != null && editingWeightId === id) {
                    return (
                        <Space className="admin-table-inline-editor" size={4}>
                            <ProductWeightInput
                                aria-label="团购商品权重值"
                                autoFocus
                                controls={false}
                                max={10000}
                                min={0}
                                precision={0}
                                value={editingWeightValue}
                                onChange={value => setEditingWeightValue(value ?? "")}
                            />
                            <Button
                                aria-label="保存团购商品权重"
                                size="small"
                                type="link"
                                onClick={() => {
                                    const weight = Number(editingWeightValue);
                                    if (editingWeightValue === "" || !Number.isFinite(weight)) return;
                                    setEditingWeightId(null);
                                    void saveWeight({ id, weight });
                                }}
                            >
                                保存
                            </Button>
                            <Button
                                aria-label="取消团购商品权重"
                                size="small"
                                type="link"
                                onClick={() => setEditingWeightId(null)}
                            >
                                取消
                            </Button>
                        </Space>
                    );
                }
                return (
                    <Space size={4}>
                        <span>{row.productGroupPurchase.weight ?? 0}</span>
                        {id != null ? (
                            <Button
                                aria-label="编辑团购商品权重值"
                                icon={<i aria-hidden="true" className="iconfont icon-edit" />}
                                size="small"
                                type="link"
                                onClick={() => {
                                    setEditingWeightValue(row.productGroupPurchase.weight ?? "");
                                    setEditingWeightId(id);
                                }}
                            />
                        ) : null}
                    </Space>
                );
            }
        },
        {
            key: "title",
            label: "主图/标题",
            minWidth: 260,
            prop: "imgMainId",
            render: ({ row }) => (
                <TableImageCell
                    alt="团购商品主图"
                    src={row.mainFile?.path}
                    title={row.productGroupPurchase.title || "--"}
                />
            )
        },
        {
            key: "productType",
            label: "品类",
            minWidth: 120,
            prop: "productTypeId",
            render: ({ row }) =>
                row.productType?.name
                    ? `${row.productType.name}${row.productType.areaAbbreviation ? ` (${row.productType.areaAbbreviation})` : ""}`
                    : "--"
        },
        {
            key: "supplier",
            label: "供应商",
            minWidth: 160,
            prop: "supplierId",
            render: ({ row }) =>
                row.productGroupPurchase.supplierEnterprise?.enterpriseName ||
                row.productGroupPurchase.supplierEnterprise?.supplierName ||
                getEnterpriseName(row.productGroupPurchase.supplierEnterprise as never)
        },
        {
            key: "groupUnitPrice",
            label: "拼团单价（元/吨）",
            width: 148,
            align: "right",
            headerAlign: "right",
            showOverflowTooltip: false,
            prop: "groupUnitPrice",
            render: ({ row }) => {
                const { groupUnitPrice, priceType, unitPrice } = row.productGroupPurchase;
                if (!groupUnitPrice && !unitPrice) return "--";
                return (
                    <div className="flex flex-col items-end">
                        <span>
                            {groupUnitPrice
                                ? `${formatCurrency(groupUnitPrice)}${priceType === 2 ? "（预估价格）" : ""}`
                                : "--"}
                        </span>
                        {unitPrice ? (
                            <span className="text-xs text-gray-400 line-through">{formatCurrency(unitPrice)}</span>
                        ) : null}
                    </div>
                );
            }
        },
        {
            key: "minOrderTon",
            label: "拼团最低吨数（吨）",
            width: 162,
            align: "right",
            headerAlign: "right",
            prop: "productGroupPurchase.minOrderTon",
            render: ({ row }) =>
                row.productGroupPurchase.minOrderTon ? formatCurrency(row.productGroupPurchase.minOrderTon) : "--"
        },
        {
            key: "groupSuccessTon",
            label: "拼团成功吨数（吨）",
            width: 162,
            align: "right",
            headerAlign: "right",
            prop: "productGroupPurchase.groupSuccessTon",
            render: ({ row }) =>
                row.productGroupPurchase.groupSuccessTon
                    ? formatCurrency(row.productGroupPurchase.groupSuccessTon)
                    : "--"
        },
        {
            key: "groupEndTime",
            label: "拼团截止时间",
            width: 150,
            sortable: true,
            prop: "groupEndTime",
            render: ({ row }) => dateTimeFormat(row.productGroupPurchase.groupEndTime)
        },
        {
            key: "progress",
            label: "拼团进度",
            width: 148,
            align: "right",
            headerAlign: "right",
            showOverflowTooltip: false,
            prop: "progress",
            render: ({ row }) => {
                if (!row.productGroupPurchase.groupPurchaseState) return "--";
                const orderedTon = Number(row.productGroupPurchase.orderedTon || 0);
                const groupSuccessTon = Number(row.productGroupPurchase.groupSuccessTon || 0);
                const percent = groupSuccessTon
                    ? Math.min(Number(((orderedTon / groupSuccessTon) * 100).toFixed(2)), 100)
                    : 0;
                return (
                    <div className="w-full text-right">
                        <div className="text-xs">
                            {formatCurrency(orderedTon / 10000)}万吨/{formatCurrency(groupSuccessTon / 10000)}万吨
                        </div>
                        <Progress percent={percent} showInfo={false} size="small" />
                    </div>
                );
            }
        },
        {
            key: "handlerName",
            label: "商品专员",
            width: 100,
            prop: "productGroupPurchase.handlerName",
            render: ({ row }) => row.productGroupPurchase.handlerName || "--"
        },
        {
            key: "updatedTime",
            label: "更新时间",
            width: 150,
            sortable: true,
            prop: "updatedTime",
            render: ({ row }) => dateTimeFormat(row.productGroupPurchase.updatedTime)
        },
        {
            key: "groupPurchaseState",
            label: "团购状态",
            width: 90,
            prop: "productGroupPurchase.groupPurchaseState",
            render: ({ row }) => <Tag>{groupStateLabels[row.productGroupPurchase.groupPurchaseState || 0] || "--"}</Tag>
        },
        {
            key: "state",
            label: "上架状态",
            width: 90,
            prop: "productGroupPurchase.state",
            render: ({ row }) => <Tag>{productStateLabels[row.productGroupPurchase.state || 0] || "--"}</Tag>
        },
        {
            key: "publishState",
            label: "发布状态",
            width: 90,
            prop: "productGroupPurchase.publishState",
            render: ({ row }) => <Tag>{publishStateLabels[row.productGroupPurchase.publishState || 0] || "--"}</Tag>
        },
        {
            key: "operation",
            label: "操作",
            width: 230,
            fixed: "right",
            prop: "operation",
            render: ({ row }) => {
                const id = row.productGroupPurchase.id;
                const publishState = row.productGroupPurchase.publishState || 0;
                const productState = row.productGroupPurchase.state;
                const groupState = row.productGroupPurchase.groupPurchaseState;
                const canAssign = canManage && !(productState === 2 && publishState === 5);
                const canAudit = canManage && publishState === 1;
                const canEdit = canDeal && productState === 2 && [1, 2, 4, 6].includes(publishState);
                const canClose = canClosePermission && productState === 2 && [1, 2, 4, 6].includes(publishState);
                const canDownShelf = canDeal && productState === 1 && [2, 3].includes(publishState) && groupState === 1;
                const canCopy = canDeal && productState === 2 && publishState === 5 && [2, 3].includes(groupState || 0);
                if (!canAssign && !canAudit && !canEdit && !canClose && !canDownShelf && !canCopy) return null;
                return (
                    <Space>
                        {canAssign ? (
                            <ZhButton
                                aria-label="变更指派"
                                disabled={banButton}
                                size="small"
                                onClick={() => openAssign(row)}
                            >
                                变更指派
                            </ZhButton>
                        ) : null}
                        {canAudit ? (
                            <ZhButton
                                aria-label="审核商品"
                                disabled={banButton}
                                size="small"
                                onClick={() =>
                                    void navigate(
                                        `/productManagement/groupPurchase/audit?id=${id || ""}&time=${Date.now()}`
                                    )
                                }
                            >
                                审核
                            </ZhButton>
                        ) : null}
                        {canEdit ? (
                            <ZhButton
                                aria-label="编辑团购商品"
                                disabled={banButton}
                                size="small"
                                onClick={() =>
                                    void navigate(
                                        `/productManagement/groupPurchase/edit?id=${id || ""}&time=${Date.now()}`
                                    )
                                }
                            >
                                编辑
                            </ZhButton>
                        ) : null}
                        {canClose ? (
                            <ZhButton
                                aria-label="关闭团购商品"
                                disabled={banButton}
                                size="small"
                                onClick={() => void handleClose(row)}
                            >
                                关闭
                            </ZhButton>
                        ) : null}
                        {canDownShelf ? (
                            <ZhButton
                                aria-label="下架团购商品"
                                disabled={banButton}
                                size="small"
                                onClick={() => void openDownShelf(id)}
                            >
                                下架
                            </ZhButton>
                        ) : null}
                        {canCopy ? (
                            <ZhButton
                                aria-label="复制团购商品"
                                disabled={banButton}
                                size="small"
                                onClick={() =>
                                    void navigate(
                                        `/productManagement/groupPurchase/add?copyId=${id || ""}&time=${Date.now()}`
                                    )
                                }
                            >
                                复制
                            </ZhButton>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    const refresh = async () => {
        const result = await load(query);
        setItems(normalizeGroupPurchaseRows(result.list));
        setTotal(result.total || 0);
    };

    const handleClose = async (row: GroupPurchaseRow) => {
        const id = row.productGroupPurchase.id;
        if (id == null) return;
        // Vue 关闭接口要求清理主图、视频和附加图片的文件 ID。
        const unActiveFileIds = [
            row.mainFile?.id,
            row.videoFile?.id,
            ...(row.otherImgFile || []).map(file => file.id)
        ].filter((fileId): fileId is number => fileId != null);
        try {
            await close(id, unActiveFileIds);
            await refresh();
        } catch {
            // 关闭接口或刷新失败时保留当前团购商品列表，并反馈 Vue 页面同类操作错误。
            setErrorMessage("团购商品关闭失败");
        }
    };

    // 沿用 Vue groupTakeDownDialog：点击列表下架只打开提示并加载参团订单统计。
    const openDownShelf = async (id?: number | string) => {
        if (id == null) return;
        setDownShelfTargetId(id);
        setDownShelfInfo(undefined);
        setDownShelfError(null);
        setDownShelfLoading(true);
        try {
            setDownShelfInfo(await loadInfo(id));
        } catch {
            // 统计查询失败时保留确认入口，并把失败状态限制在当前弹窗内。
            setDownShelfError("团购下架订单信息加载失败");
        } finally {
            setDownShelfLoading(false);
        }
    };

    const cancelDownShelf = () => {
        setDownShelfTargetId(null);
        setDownShelfInfo(undefined);
        setDownShelfError(null);
    };

    const confirmDownShelf = async () => {
        if (downShelfTargetId == null) return;
        setDownShelfLoading(true);
        try {
            await removeFromShelf(downShelfTargetId);
            await refresh();
            cancelDownShelf();
        } catch {
            // 下架或刷新接口失败时保留弹窗，并反馈与 Vue 页面一致的失败状态。
            setDownShelfError("团购商品下架失败");
        } finally {
            setDownShelfLoading(false);
        }
    };

    const searchSpecialists = async (keyword: string) => {
        setAssignLoading(true);
        try {
            setAssignError(null);
            const nextSpecialists = await findSpecials(keyword);
            setSpecialists(nextSpecialists);
            return nextSpecialists;
        } catch {
            setAssignError("专员查询失败");
            setSpecialists([]);
        } finally {
            setAssignLoading(false);
        }
    };

    const openAssign = (row: GroupPurchaseRow) => {
        setAssignTarget(row);
        setSelectedHandlerId(row.productGroupPurchase.handlerId);
        void searchSpecialists("");
    };

    const handleAssign = async ({ officerId }: assignSubmitData) => {
        if (!assignTarget || officerId == null) {
            setAssignError("请选择指派的服务人员");
            return;
        }
        const id = assignTarget.productGroupPurchase.id;
        if (id == null) return;
        setAssignLoading(true);
        try {
            await assign(id, officerId);
            setAssignTarget(null);
            await refresh();
        } catch (error) {
            setAssignError("变更指派失败");
            throw error;
        } finally {
            setAssignLoading(false);
        }
    };

    return (
        <section aria-label="group-purchase-page" className="flex h-full min-h-full flex-col">
            <ZhDialog
                open={downShelfTargetId != null}
                title="提示"
                width={400}
                onCancel={cancelDownShelf}
                footer={
                    <Space>
                        <Button disabled={downShelfLoading} onClick={cancelDownShelf}>
                            取消
                        </Button>
                        <Button
                            aria-label="确认下架"
                            loading={downShelfLoading}
                            type="primary"
                            onClick={() => void confirmDownShelf()}
                        >
                            确认
                        </Button>
                    </Space>
                }
            >
                <p>是否确认下架，下架后已参团订单将会全部取消</p>
                {downShelfInfo?.totalOrderNum ? (
                    <div className="mt-3 rounded-md bg-gray-100 p-4">
                        目前有 <span className="text-red-500">{downShelfInfo.totalOrderNum}</span> 单参与，
                        <span className="text-red-500">{downShelfInfo.obligationOrderNum || 0}</span> 单待付款，
                        <span className="text-red-500">{downShelfInfo.toBeConfirmedOrderNum || 0}</span> 单待确认支付，
                        <span className="text-red-500">{downShelfInfo.groupingOrderNum || 0}</span> 单拼团中
                    </div>
                ) : null}
                {downShelfError ? <Alert message={downShelfError} role="alert" type="error" /> : null}
            </ZhDialog>
            <AssignDialog
                open={Boolean(assignTarget)}
                info={assignTarget || undefined}
                title="变更指派商品专员"
                confirmText="确认"
                officers={specialists as Officer[]}
                searchSpecial={searchSpecialists}
                tips="跟进的商品数量"
                countFiled="productCount"
                countSuffix="个"
                curOfficerId={selectedHandlerId}
                loading={assignLoading}
                error={assignError}
                onClose={() => {
                    setAssignTarget(null);
                    setAssignError(null);
                }}
                onCurOfficerIdChange={setSelectedHandlerId}
                onSubmit={handleAssign}
            />
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<GroupPurchaseRow>
                    data={items}
                    columns={columns}
                    pagination={false}
                    rowKey={row => String(row.productGroupPurchase.id)}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                团购商品管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                initialValues={{ publishState: query.publishState || [] }}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                {canDeal ? (
                                    <Button
                                        aria-label="新增团购商品"
                                        className="admin-table-primary-action"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() =>
                                            void navigate(`/productManagement/groupPurchase/add?time=${Date.now()}`)
                                        }
                                    >
                                        新增商品
                                    </Button>
                                ) : null}
                                <Form.Item className="product-filter-keyword" name="keyword">
                                    <Input placeholder="请输入商品编号或商品标题" />
                                </Form.Item>
                                <Form.Item className="product-filter-category" name="productTypeId">
                                    <Select
                                        allowClear
                                        aria-label="团购品类"
                                        filterOption={false}
                                        loading={productTypeLoading}
                                        options={productTypeOptions}
                                        placeholder="请选择品类"
                                        showSearch
                                        onSearch={value => void searchProductTypes(value)}
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-supplier" name="supplierId">
                                    <Select
                                        allowClear
                                        aria-label="团购供应商"
                                        filterOption={false}
                                        loading={supplierLoading}
                                        options={supplierOptions}
                                        placeholder="请选择供应商"
                                        showSearch
                                        onSearch={value => void searchSuppliers(value)}
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-state" name="groupPurchaseState">
                                    <Select
                                        allowClear
                                        aria-label="团购状态"
                                        options={Object.entries(groupStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择团购状态"
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-state" name="state">
                                    <Select
                                        allowClear
                                        aria-label="上架状态"
                                        options={Object.entries(productStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择上架状态"
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-publish-state" name="publishState">
                                    <Select
                                        allowClear
                                        aria-label="发布状态"
                                        maxTagCount={1}
                                        mode="multiple"
                                        options={Object.entries(publishStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        placeholder="请选择发布状态"
                                        onChange={values => clearDaiBanState(values, "groupPublishState")}
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-handler" name="handlerName">
                                    <Input placeholder="请输入商品专员搜索" />
                                </Form.Item>
                                <Button
                                    aria-label="搜索"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={resetFilters}
                                />
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                showTotal={value => `共${value}条数据`}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                // 沿用 Vue 团购商品分页契约，切换页大小后回到第 1 页。
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default GroupPurchasePage;
